import {
    AutocompleteInput,
    BooleanInput,
    Breadcrumb,
    Create,
    DateInput,
    NumberInput,
    ReferenceInput,
    SimpleForm,
    TextInput,
} from "@/components/admin";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { FileDown, FileText, Landmark, Truck } from "lucide-react";
import { useGetList, useNotify, useRedirect } from "ra-core";
import { useWatch } from 'react-hook-form';
import { FilteredContactInput } from '../../misc/FilteredContactInput';
import { Link, useLocation } from "react-router-dom";
import { AutoPopulateCompanyDetails } from '../shared/AutoPopulateCompanyDetails';
import { AutoReferenceField } from '../shared/AutoReferenceField';
import { CustomerNameWatcher } from '../shared/CustomerNameWatcher';
import { QuotationWatcher } from '../shared/QuotationWatcher';
import { DocumentItemsTable } from '../shared/DocumentItemsTable';
import { GrandTotalsWatcher } from '../shared/GrandTotalsWatcher';
import { TermsTemplateSelector } from '../shared/TermsTemplateSelector';
import { requireNonZeroTotal } from '../shared/validators';

/** Must live inside SimpleForm so useWatch binds to the form context */
const LogisticsFields = () => {
    const includeLogistics = useWatch({ name: 'include_logistics' });
    if (!includeLogistics) return null;
    return (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 pt-4">
            <TextInput source="lr_number" label="LR Number" className="w-full" />
            <TextInput source="transporter_name" label="Transporter Name" className="w-full" />
            <NumberInput source="lr_amount" label="Freight (LR) Amount" className="w-full" />
            <TextInput source="delivery_terms" label="Delivery Terms" className="w-full" />
            <TextInput source="dispatch_through" label="Dispatch Through" className="w-full" />
            <TextInput source="destination" label="Destination" className="w-full" />
        </div>
    );
};

export const PICreate = () => {
    const location = useLocation();
    const sourceRecord = location.state?.record || {};
    const redirect = useRedirect();
    const notify = useNotify();

    const { data: terms } = useGetList('terms_templates', {
        pagination: { page: 1, perPage: 1 },
        filter: { is_default: true }
    });
    
    const defaultTerms = terms && terms.length > 0 ? terms[0].content : '';

    const createTitle = (
        <span className="text-xl font-bold tracking-tight">New Proforma Invoice</span>
    );

    return (
        <Create disableBreadcrumb
            record={{
                terms_conditions: defaultTerms,
                items: [{ description: '', hsn_code: '', quantity: 1, unit: 'Nos', unit_price: 0, tax_rate: 18, line_total: 0 }],
                ...sourceRecord,
            }}
            title={createTitle}
            mutationOptions={{
                onSuccess: (data: any) => {
                    notify("ra.notification.created", { type: "info", messageArgs: { smart_count: 1 } });
                    redirect(`/proforma_invoices/${data.id}/show`);
                },
            }}
        >
            <Breadcrumb>
                <Breadcrumb.Item><Link to="/proforma_invoices">Proforma Invoices</Link></Breadcrumb.Item>
                <Breadcrumb.PageItem>Create</Breadcrumb.PageItem>
            </Breadcrumb>
            <SimpleForm className="max-w-5xl mx-auto space-y-6" validate={requireNonZeroTotal}>
                <AutoPopulateCompanyDetails />
                <QuotationWatcher />
                <Card className="w-full shadow-sm">
                    <CardHeader className="pb-3 border-b bg-muted/50">
                        <div className="flex items-center gap-2">
                            <FileDown className="w-5 h-5 text-primary" />
                            <CardTitle className="text-lg font-semibold tracking-tight">New Proforma Invoice</CardTitle>
                        </div>
                    </CardHeader>
                    <CardContent className="pt-6">
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                            <AutoReferenceField docType="proforma_invoice" />
                            <DateInput source="date" defaultValue={new Date()} className="w-full" />
                            
                            <ReferenceInput source="quotation_id" reference="quotations">
                                <AutocompleteInput label="Related Quotation" className="w-full" />
                            </ReferenceInput>

                            <ReferenceInput source="company_id" reference="companies">
                                <AutocompleteInput label="Company" className="w-full" />
                            </ReferenceInput>
                            <FilteredContactInput source="customer_id" label="Customer Contact" className="w-full" />

                            <TextInput source="customer_name" label="Customer Name Override" className="w-full" />
                            <TextInput source="project_id" className="hidden" />
                        </div>
                        {/* PI-1: Bill To / Ship To — auto-filled from company master, editable */}
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 pt-4">
                            <TextInput source="billing_address" label="Billing Address (Bill To)" multiline rows={3} className="w-full" helperText="Auto-filled from company; edit to override" />
                            <TextInput source="shipping_address" label="Shipping Address (Ship To)" multiline rows={3} className="w-full" helperText="Auto-filled from company; edit to override" />
                        </div>
                    </CardContent>
                </Card>

                <Card className="w-full shadow-sm">
                    <CardHeader className="pb-3 border-b">
                        <div className="flex items-center justify-between w-full">
                            <div className="flex items-center gap-2">
                                <Truck className="w-5 h-5 text-muted-foreground" />
                                <CardTitle className="text-lg font-semibold tracking-tight">Logistics (LR Details)</CardTitle>
                            </div>
                            <BooleanInput
                                source="include_logistics"
                                label="Include Logistics"
                                helperText={false}
                            />
                        </div>
                    </CardHeader>
                    <CardContent>
                        <LogisticsFields />
                    </CardContent>
                </Card>


                <Card className="w-full shadow-sm">
                    <CardHeader className="pb-3 border-b">
                        <div className="flex items-center gap-2">
                            <Landmark className="w-5 h-5 text-primary" />
                            <CardTitle className="text-lg font-semibold tracking-tight">Line Items & Financials</CardTitle>
                        </div>
                    </CardHeader>
                    <CardContent className="pt-6 space-y-6">
                        <DocumentItemsTable showSubItems />
                        <GrandTotalsWatcher />
                        <CustomerNameWatcher />

                        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-4 bg-muted p-4 rounded-lg border">
                            <NumberInput source="cgst_amount" label="CGST (₹)" readOnly className="w-full" />
                            <NumberInput source="sgst_amount" label="SGST (₹)" readOnly className="w-full" />
                            <NumberInput source="igst_amount" label="IGST (₹)" helperText="Enter manually for interstate" className="w-full" />
                            <NumberInput source="total_amount" label="Grand Total (₹)" readOnly className="w-full font-bold text-primary" />
                            <div className="md:col-span-4">
                                <TextInput source="amount_in_words" label="Amount in Words" className="w-full italic" readOnly />
                            </div>
                        </div>
                    </CardContent>
                </Card>

                <Card className="w-full shadow-sm">
                    <CardHeader className="pb-3 border-b bg-muted/50">
                        <div className="flex items-center justify-between w-full">
                            <div className="flex items-center gap-2">
                                <FileText className="w-5 h-5 text-primary" />
                                <CardTitle className="text-lg font-semibold tracking-tight">Terms & Notes</CardTitle>
                            </div>
                            <TermsTemplateSelector type="quotation" />
                        </div>
                    </CardHeader>
                    <CardContent className="pt-6">
                        <TextInput source="terms_conditions" label="Terms & Conditions" multiline rows={4} className="w-full max-w-none" />
                    </CardContent>
                </Card>
            </SimpleForm>
        </Create>
    );
};
